import React, { useState, useMemo } from "react";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import { Tenant } from "types";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { AUTURA_ASPHALT_LIGHT } from "context/themeContext";
export interface CopyTenantButtonProps {
  onSelect: (tenant: Tenant) => void;
  tenantList: Tenant[];
  loading?: boolean;
}

const CopyTenantButton: React.FC<CopyTenantButtonProps> = ({
  onSelect,
  tenantList,
  loading = false,
}) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [copied, setCopied] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    if (selectedIndex < 0) {
      return;
    }
    onSelect(tenantList[selectedIndex]);
    setCopied(true);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    setSelectedIndex(index);
    setAnchorEl(null);
    onSelect(tenantList[index]);
    setCopied(true);
  };

  const handleToggle = () => {
    setAnchorEl(anchorEl ? null : anchorEl);
  };

  const handleClose = (event: Event) => {
    if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
      return;
    }

    setAnchorEl(null);
  };

  const copyButtonText = useMemo(() => {
    let name;
    if (tenantList.length === 0 || selectedIndex < 0) {
      name = "";
    } else {
      name = tenantList[selectedIndex].name || "";
    }
    if (copied) {
      return `${t("button.copy_tenant_selected", {
        ns: "commandCenter",
        name: name,
      })}`;
    }
    return `${t("button.copy_tenant", { ns: "commandCenter", name: name })}`;
  }, [copied, selectedIndex, tenantList]);

  return (
    <Stack
      direction="column"
      spacing={0}
      sx={{ py: { xs: 2, sm: 1, lg: 0 }, width: "100%" }}
    >
      <ButtonGroup
        variant={copied ? "outlined" : "contained"}
        aria-label={t("tenant.copyButton")}
        color={"secondary"}
        disabled={tenantList.length === 0 || loading}
      >
        <Button onClick={handleClick} fullWidth sx={{ py: 1.5 }}>
          {copyButtonText}
        </Button>
        {loading ? (
          <LoadingButton
            size="small"
            aria-controls={"split-button-menu"}
            aria-expanded={"false"}
            aria-label={t("loading")}
            aria-haspopup="menu"
            loading
            onClick={handleToggle}
          />
        ) : (
          <Button
            size="small"
            aria-controls={"split-button-menu"}
            aria-expanded={open ? "true" : "false"}
            aria-label={t("tenant.copyButton")}
            aria-haspopup="menu"
            onClick={handleToggle}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 3000,
          width: 500,
        }}
        open={open}
        anchorEl={anchorEl}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              width: "100%",
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper
              sx={{
                maxHeight: 300,
                backgroundColor: "white",
                width: "100%",
                overflowY: "auto",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  id={`${t("tenant.label_plural")}-${t("menu")}`}
                  autoFocusItem
                  sx={{
                    backgroundColor: "white",
                    width: "100%",
                    border: "1px solid",
                    borderColor: AUTURA_ASPHALT_LIGHT,
                  }}
                >
                  {tenantList &&
                    tenantList.map((tenant, index) => (
                      <MenuItem
                        sx={{ backgroundColor: "white", width: "100%" }}
                        key={tenant.id}
                        disabled={index === 2}
                        selected={index === selectedIndex}
                        onClick={(event) => handleMenuItemClick(event, index)}
                        divider
                      >
                        <Typography variant="subtitle1">
                          {tenant.name}
                        </Typography>
                      </MenuItem>
                    ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Stack>
  );
};

export default CopyTenantButton;
